import { FAST_API_URL, postOptions } from "../index";
import {
    AddSubtitleArgs,
    CreateRusSubIntervalArgs,
    CreateRusSubIntervalMultiArgs,
    CreateRusSubIntervalMultiResponse,
    CreateRusSubIntervalResponse,
    DeleteSubtitleArgs,
    EditMasterRuSubtitleArgs,
    EditSubtitleArgs,
    MergeSubtitleArgs,
    OpusSplitTranslatedSubtitleArgs,
    RecreateTranslatedFromRuArgs,
    RecreateTranslatedFromRuResponse,
    SplitSubtitleArgs,
    SplitTranslatedSubtitleArgs,
} from "./types";
import { AudioTrackData } from "../../providers/AudioProvider/types";
import { extractErrorDetail, throwIfNotOk } from "./httpErrors";

// Общие для всех ручек редактора: отказ сервера — словами сервера (см. httpErrors).
export { extractErrorDetail, throwIfNotOk };



export const splitRusSubtitleApi = async ({ mediaId, ...body }: SplitSubtitleArgs) => {
    console.log("splitRusSubtitleApi", body);
    const response = await fetch(`${FAST_API_URL}/metadata_split_subtitle?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    await throwIfNotOk(response, "Split failed");
    return response.json();
};

export const deleteRusSubtitleApi = async ({ mediaId, ...body }: DeleteSubtitleArgs) => {
    const response = await fetch(`${FAST_API_URL}/metadata_trash_subtitle?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    await throwIfNotOk(response, "Delete failed");
    return response.json();
};

export const addRusSubtitleApi = async ({ mediaId, ...body }: AddSubtitleArgs) => {
    const response = await fetch(`${FAST_API_URL}/metadata_add_subtitle?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    return response.json();
};

export const mergeRusSubtitleApi = async ({
    mediaId,
    ...body
}: MergeSubtitleArgs): Promise<AudioTrackData> => {
    const response = await fetch(`${FAST_API_URL}/metadata_merge_subtitles?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    // Отказ склейки — не склеенная реплика: раньше тело ошибки шло дальше как данные, и
    // редактор просил машинный перевод реплики с id undefined.
    await throwIfNotOk(response, "Merge failed");
    return response.json();
};

export const downloadRusAudioApi = async (file_name: string) => {
    const response = await fetch(`${FAST_API_URL}/download_rus?file_name=${file_name}`, {});
    if (!response.ok) {
        throw new Error("cant download rus audio");
    }
    return response.blob();
};

export const createRusSubIntervalApi = async ({
    mediaId,
    ...body
}: CreateRusSubIntervalArgs): Promise<CreateRusSubIntervalResponse> => {
    const response = await fetch(`${FAST_API_URL}/create_rus_subs_interval?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    // «в интервале нет речи», «нет видео папки» — переводчик должен это прочесть, а не
    // нажимать «+» ещё раз.
    await throwIfNotOk(response, "Create interval failed");
    return response.json();
};

export const createRusSubIntervalMultiApi = async ({
    mediaId,
    ...body
}: CreateRusSubIntervalMultiArgs): Promise<CreateRusSubIntervalMultiResponse> => {
    const response = await fetch(
        `${FAST_API_URL}/create_rus_subs_interval_multi?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify(body),
        },
    );
    await throwIfNotOk(response, "Create interval multi failed");
    return response.json();
};

export const editRusSubtitleApi = async ({ mediaId, ...body }: EditSubtitleArgs) => {
    const response = await fetch(`${FAST_API_URL}/metadata_edit_subtitle?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    // Отказ сохранения русской реплики — не «Saved ✓»: автосохранение вернёт правку в
    // несохранённые и покажет причину.
    await throwIfNotOk(response, "Edit failed");
    return response.json();
};

/**
 * Regenerate translation + TTS for the affected cues in the pair being edited
 * (stops before merge/HLS/publish). Scoped to that one pair: it used to fan out
 * to every ru_* pair, imposing one language's wording on all the others.
 * `lang` is now required by the endpoint.
 * Fire-and-forget: never throws — a failed trigger must not break autosave.
 */
export const applyRuCorrectionApi = async ({
    mediaId,
    folderNum,
    editedIds,
    removedIds = [],
    fullFolderRebuild = false,
    lang,
    languageFrom = "ru",
}: {
    mediaId: string;
    folderNum: number;
    editedIds: string[];
    removedIds?: string[];
    fullFolderRebuild?: boolean;
    lang: string;
    languageFrom?: string;
}) => {
    try {
        const response = await fetch(`${FAST_API_URL}/apply_ru_correction?media=${mediaId}`, {
            ...postOptions,
            body: JSON.stringify({
                folder_num: folderNum,
                edited_ids: editedIds,
                removed_ids: removedIds,
                full_folder_rebuild: fullFolderRebuild,
                lang,
                language_from: languageFrom,
            }),
        });
        if (!response.ok) return null;
        return await response.json().catch(() => null);
    } catch (e) {
        console.warn("[applyRuCorrection] trigger failed", e);
        return null;
    }
};

// --- Translated-led split & merge -----------------------------------------------------
// The translated text drives the operation and the backend owns the full lifecycle
// (RU counterparts, TTS, manifests) — no follow-up delete calls from the client.

export const splitTranslatedSubtitleApi = async ({
    mediaId,
    ...body
}: SplitTranslatedSubtitleArgs) => {
    const response = await fetch(
        `${FAST_API_URL}/metadata_split_translated_subtitle?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify(body),
        },
    );
    await throwIfNotOk(response, "Split translated failed");
    return response.json();
};

export const opusSplitTranslatedSubtitleApi = async ({
    mediaId,
    ...body
}: OpusSplitTranslatedSubtitleArgs) => {
    const response = await fetch(
        `${FAST_API_URL}/metadata_split_translated_opus?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify(body),
        },
    );
    await throwIfNotOk(response, "Opus split failed");
    return response.json();
};

export const mergeTranslatedSubtitlesApi = async ({ mediaId, ...body }: MergeSubtitleArgs) => {
    const response = await fetch(
        `${FAST_API_URL}/metadata_merge_translated_subtitles?media=${mediaId}`,
        {
            ...postOptions,
            body: JSON.stringify(body),
        },
    );
    await throwIfNotOk(response, "Merge translated failed");
    return response.json();
};

export const editMasterRuSubtitleApi = async ({ mediaId, ...body }: EditMasterRuSubtitleArgs) => {
    const response = await fetch(`${FAST_API_URL}/edit_master_ru_subtitle?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    await throwIfNotOk(response, "Master edit failed");
    return response.json();
};

export const recreateTranslatedFromRuApi = async ({
    mediaId,
    ...body
}: RecreateTranslatedFromRuArgs): Promise<RecreateTranslatedFromRuResponse> => {
    const response = await fetch(`${FAST_API_URL}/recreate_translated_from_ru?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    await throwIfNotOk(response, "Recreate translated failed");
    return response.json();
};

// «Русский готов» — the editor's explicit per-folder mark that the Russian is finished;
// after it, translators take the folder over without the not-finished warning.
export const ruDoneSetApi = async ({ mediaId, ...body }: import("./types").RuDoneArgs) => {
    const response = await fetch(`${FAST_API_URL}/ru_done?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    await throwIfNotOk(response, "ru_done failed");
    return response.json();
};

export const ruDoneGetApi = async (mediaId: string) => {
    const response = await fetch(`${FAST_API_URL}/ru_done?media=${mediaId}`);
    await throwIfNotOk(response, "ru_done read failed");
    return response.json();
};

export const translationDoneSetApi = async ({
    mediaId,
    ...body
}: import("./types").TranslationDoneArgs) => {
    const response = await fetch(`${FAST_API_URL}/translation_done?media=${mediaId}`, {
        ...postOptions,
        body: JSON.stringify(body),
    });
    await throwIfNotOk(response, "translation_done failed");
    return response.json();
};

export const translationDoneGetApi = async (
    mediaId: string,
    lang: string,
    languageFrom = "ru",
) => {
    const params = new URLSearchParams({ media: mediaId, lang, language_from: languageFrom });
    const response = await fetch(`${FAST_API_URL}/translation_done?${params.toString()}`);
    await throwIfNotOk(response, "translation_done read failed");
    return response.json();
};
